/* eslint-disable static-service/require-stories -- we have stories for the Markdown component */
import {StyleSheet} from "aphrodite";

import SimpleMarkdown, {type State} from "@khanacademy/simple-markdown";
import {addStyle} from "@khanacademy/wonder-blocks-core";

import type {MarkdownRule} from "../types.ts";

import {NON_PARAGRAPH_BLOCK_MATCHERS} from "./rule-utils.ts";
import {blockStyle} from "./shared-styles.ts";

const StyledP = addStyle("p");

// matchParagraph is the matcher function that captures lines of text to
// include in paragraph blocks and stops when it encounters a non-paragraph
// block syntax.
const matchParagraph = (source: string, state: State, prevCapture: string) => {
    // If we're matching for inline scoped elements, then we won't match for
    // paragraphs.
    if (state.inline) {
        return null;
    }
    // We still want to support line-breaks within paragraphs so we don't
    // match continuation of a line.
    if (prevCapture && !prevCapture.endsWith("\n")) {
        return null;
    }

    let match = "";
    // Iterate with Array.every so that we can short-circuit.
    source.split("\n").every((line) => {
        // Compare the remaining unmatched lines to see if there is a sign
        // of a non-paragraph block. If we end up matching any of the block
        // regex patterns, then we'll end our paragraph match with the lines
        // matched thus far.
        const remainingLines = source.substr(match.length);
        if (
            NON_PARAGRAPH_BLOCK_MATCHERS.some(
                (matchFn) =>
                    !!matchFn(remainingLines, {...state, inline: false}, match),
            )
        ) {
            return false;
        }
        match += line + "\n";
        return line.trim();
    });

    const captured = match.trimEnd();
    if (captured === "") {
        return null;
    }
    return [match, captured];
};

export const paragraphRule: Readonly<MarkdownRule> = {
    ...SimpleMarkdown.defaultRules.paragraph,
    match: matchParagraph,
    react: (node, output, state) => (
        <StyledP key={state.key} style={styles.paragraph}>
            {output(node.content)}
        </StyledP>
    ),
};

const styles = StyleSheet.create({
    paragraph: {
        // @ts-expect-error - TS2783 - 'margin' is specified more than once, so this usage will be overwritten.
        margin: 0,
        whiteSpace: "pre-wrap",
        fontSize: "inherit",
        ...blockStyle,
    },
});
